import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Image as ImageIcon, Sparkles, Video, Wand2 } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { ConsentModal } from "@/components/create/ConsentModal";
import { TEMPLATES } from "@/lib/templates";

export const Route = createFileRoute("/_authenticated/create/")({
  head: () => ({
    meta: [
      { title: "新規作成 | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CreateSelectPage,
});

function CreateSelectPage() {
  const navigate = useNavigate();
  const [verified, setVerified] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      const uid = data.user?.id ?? null;
      if (!uid) {
        if (!cancelled) setVerified(true);
        return;
      }
      const { data: profile } = await supabase
        .from("profiles")
        .select("verified")
        .eq("user_id", uid)
        .maybeSingle();
      if (!cancelled) setVerified(Boolean(profile?.verified));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <ConsentModal
        open={verified === false}
        onAgreed={() => setVerified(true)}
        onCancel={() => navigate({ to: "/dashboard" })}
      />
      <main className="min-h-screen bg-background px-4 py-12 sm:py-16">
        <div className="mx-auto max-w-4xl">
          <header className="mb-10 text-center">
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
              何を作りますか？
            </h1>
            <p className="mt-3 text-sm text-muted-foreground sm:text-base">
              画像生成または動画生成を選んでください。
            </p>
          </header>

          {/* 変身テンプレ CTA — ライト層向け 3ステップ */}
          <Link
            to="/create/template"
            className="group relative mb-6 block overflow-hidden rounded-2xl border-2 border-primary/60 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent p-5 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-2xl sm:p-7"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-brand text-white shadow-lg">
                <Sparkles className="h-7 w-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-bold sm:text-xl">3ステップで変身動画を作る</h2>
                  <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground">
                    かんたん
                  </span>
                </div>
                <p className="text-xs text-muted-foreground sm:text-sm">
                  自撮り・背景・変身対象の3枚をアップするだけ。AIが自動で合成して動画化します。
                </p>
              </div>
              <ArrowRight className="h-5 w-5 shrink-0 text-primary transition-transform group-hover:translate-x-1" />
            </div>
          </Link>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Link
              to="/create/image"
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition hover:-translate-y-1 hover:border-primary hover:shadow-xl sm:p-8"
            >
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600 text-white shadow-lg">
                <ImageIcon className="h-7 w-7" />
              </div>
              <div className="mb-2 flex items-center gap-2">
                <h2 className="text-xl font-bold">画像を作る</h2>
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary">
                  NEW
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                プロンプトを入力するだけでAIが画像を生成。1クレジットから試せます。
              </p>
              <div className="mt-6 flex items-center text-sm font-semibold text-primary">
                はじめる
                <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
              <Wand2 className="absolute right-4 top-4 h-5 w-5 text-muted-foreground/30" />
            </Link>

            <Link
              to="/create/video"
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition hover:-translate-y-1 hover:border-primary hover:shadow-xl sm:p-8"
            >
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-brand text-white shadow-lg">
                <Video className="h-7 w-7" />
              </div>
              <div className="mb-2 flex items-center gap-2">
                <h2 className="text-xl font-bold">動画を作る</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                人物画像と動画素材をアップロードして、AIで変身動画を生成します。
              </p>
              <div className="mt-6 flex items-center text-sm font-semibold text-primary">
                はじめる
                <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          </div>

          {/* テンプレ別ショートカット（一時非表示）
          <div className="mt-10">
            <p className="mb-3 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              テンプレートから始める
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {TEMPLATES.map((t) => (
                <Link
                  key={t.id}
                  to="/create/template"
                  search={{ template: t.id } as never}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition hover:border-primary hover:bg-primary/10 hover:text-primary"
                >
                  <t.icon className="h-4 w-4" />
                  {t.title}
                </Link>
              ))}
            </div>
          </div>
          */}
        </div>
      </main>
    </>
  );
}
